import { useCallback, useEffect, useRef, useState } from 'react';

import {
  clampScrollLeft,
  getMaxScrollLeft,
  resolveHorizontalWheelDelta,
} from './useHorizontalDragScroll.utils';

// Pointer travel (px) before a press turns into a scroll drag. Below it the
// press stays a click/tap so overlaid controls (buttons, cards) keep working.
const DRAG_ACTIVATION_PX = 5;

// The normalized horizontal-scroll contract every zone (funnel board, records
// journal) exposes. The zone owns HOW it scrolls (native `scrollLeft` vs a
// `transform` track); this model hides that from the hook and the minimap.
export interface HorizontalScrollModel {
  contentWidth: number;
  viewportWidth: number;
  scrollLeft: number;
  onScrollTo: (nextScrollLeft: number) => void;
}

interface UseHorizontalDragScrollParams {
  model: HorizontalScrollModel;
  enabled?: boolean;
  // Wheel horizontalization. Leave off where the element scrolls natively
  // (`overflow-x: auto`), otherwise trackpad `deltaX` would be applied twice.
  withWheel?: boolean;
  // Pointer/touch drag-to-scroll. Set `touch-action` on the element in CSS
  // (e.g. `pan-y`) so the browser yields the horizontal gesture.
  withDrag?: boolean;
  // Guard drag start so it does not hijack draggable children (appointment
  // cards, dnd handles): return false for those targets.
  canStartDrag?: (target: EventTarget | null) => boolean;
}

export const useHorizontalDragScroll = <T extends HTMLElement>({
  model,
  enabled = true,
  withWheel = true,
  withDrag = true,
  canStartDrag,
}: UseHorizontalDragScrollParams) => {
  // Node state (not a bare ref) so the listener effect re-runs when the target
  // element mounts/remounts. `ref` is a callback ref, composable with others.
  const [element, setElement] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => setElement(node), []);

  // Latest model/predicate read at event time so the listeners stay stable and
  // are not re-attached on every scroll frame.
  const modelRef = useRef(model);
  const canStartDragRef = useRef(canStartDrag);

  useEffect(() => {
    modelRef.current = model;
    canStartDragRef.current = canStartDrag;
  });

  useEffect(() => {
    if (!element || !enabled) {
      return undefined;
    }

    const scrollTo = (next: number): void => {
      const { contentWidth, viewportWidth, onScrollTo } = modelRef.current;

      onScrollTo(
        clampScrollLeft(next, getMaxScrollLeft(contentWidth, viewportWidth))
      );
    };

    const hasOverflow = (): boolean => {
      const { contentWidth, viewportWidth } = modelRef.current;

      return getMaxScrollLeft(contentWidth, viewportWidth) > 0;
    };

    const handleWheel = (event: WheelEvent): void => {
      if (!hasOverflow()) {
        return;
      }

      const { viewportWidth, scrollLeft } = modelRef.current;
      const delta = resolveHorizontalWheelDelta(event, viewportWidth);

      if (delta === 0) {
        return;
      }

      event.preventDefault();
      scrollTo(scrollLeft + delta);
    };

    let activePointerId: number | null = null;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let isDragging = false;

    const handlePointerDown = (event: PointerEvent): void => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      if (!hasOverflow()) {
        return;
      }

      if (canStartDragRef.current && !canStartDragRef.current(event.target)) {
        return;
      }

      // Track the press but do NOT capture yet: capture only after the pointer
      // travels past the activation threshold, so a plain click still reaches
      // the child underneath.
      activePointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScrollLeft = modelRef.current.scrollLeft;
      isDragging = false;
    };

    const handlePointerMove = (event: PointerEvent): void => {
      if (activePointerId === null || event.pointerId !== activePointerId) {
        return;
      }

      const deltaX = event.clientX - dragStartX;

      if (!isDragging) {
        if (Math.abs(deltaX) < DRAG_ACTIVATION_PX) {
          return;
        }

        isDragging = true;
        element.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
      scrollTo(dragStartScrollLeft - deltaX);
    };

    const endDrag = (event: PointerEvent): void => {
      if (activePointerId === null || event.pointerId !== activePointerId) {
        return;
      }

      if (element.hasPointerCapture(event.pointerId)) {
        element.releasePointerCapture(event.pointerId);
      }

      activePointerId = null;
      isDragging = false;
    };

    if (withWheel) {
      element.addEventListener('wheel', handleWheel, { passive: false });
    }

    if (withDrag) {
      element.addEventListener('pointerdown', handlePointerDown);
      element.addEventListener('pointermove', handlePointerMove);
      element.addEventListener('pointerup', endDrag);
      element.addEventListener('pointercancel', endDrag);
    }

    return () => {
      if (withWheel) {
        element.removeEventListener('wheel', handleWheel);
      }

      if (withDrag) {
        element.removeEventListener('pointerdown', handlePointerDown);
        element.removeEventListener('pointermove', handlePointerMove);
        element.removeEventListener('pointerup', endDrag);
        element.removeEventListener('pointercancel', endDrag);
      }
    };
  }, [element, enabled, withWheel, withDrag]);

  return { ref };
};
